import type {
  DingTongConfig,
  DingTongItem,
  DingTongModuleSetting,
  DingTongPageConfig,
} from '@/api/types/ding-tong';

export const DING_TONG_PAGE = {
  HOME: 'home',
  LIST_ACTIVITY: 'list_activity',
  PRODUCT_DETAIL: 'product_detail',
  CART: 'cart',
} as const;

export type DingTongPage = (typeof DING_TONG_PAGE)[keyof typeof DING_TONG_PAGE];

export interface ResolvedDingTong {
  items: DingTongItem[];
  page: DingTongPage;
  moduleSetting: DingTongModuleSetting;
}

const MAX_VISIBLE_ITEMS = 3;
const HEX_COLOR_PATTERN = /^#(?:[\da-f]{3}|[\da-f]{4}|[\da-f]{6}|[\da-f]{8})$/i;

export const DING_TONG_BACKGROUND_COLOR = '#000000';
export const DEFAULT_DING_TONG_BACKGROUND_COLOR = DING_TONG_BACKGROUND_COLOR;
export const DEFAULT_DING_TONG_COUNTDOWN_COLOR = 'var(--color-primary-black)';

/** 新版顶通背景固定为纯黑色，不读取后台的 item 或模块背景色。 */
export function getDingTongBackgroundColor() {
  return DING_TONG_BACKGROUND_COLOR;
}

function matchesRoute(pathname: string, route: string) {
  return pathname === route || pathname.startsWith(`${route}/`);
}

function toMilliseconds(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isDingTongPageConfig(value: unknown): value is DingTongPageConfig {
  return isRecord(value);
}

function hasValue(value: unknown) {
  return (
    (typeof value === 'string' && value.trim().length > 0) ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

/** 后台颜色选择器当前约定返回十六进制色值，其他输入统一回退设计系统颜色。 */
export function getSafeDingTongColor(
  value: unknown,
  fallback: string = DEFAULT_DING_TONG_BACKGROUND_COLOR,
) {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value.trim())
    ? value.trim()
    : fallback;
}

/** 只校验会影响稳定展示和生命周期计算的必需字段。 */
export function isValidDingTongItem(value: unknown): value is DingTongItem {
  if (!isRecord(value)) return false;
  if (typeof value.dt_text !== 'string' || value.dt_text.trim().length === 0) return false;
  if (toMilliseconds(value.server_time as number | string | null | undefined) === null)
    return false;

  const deadline = value.deadline_time_TUC_5_unix;
  if (deadline !== null && toMilliseconds(deadline as number | string | undefined) === null) {
    return false;
  }

  return (
    value.shop_now_text === undefined ||
    value.shop_now_text === null ||
    typeof value.shop_now_text === 'string'
  );
}

/** Shop Now 只由后台文案控制，与倒计时和跳转配置相互独立。 */
export function hasDingTongShopNowText(item: DingTongItem) {
  return typeof item.shop_now_text === 'string' && item.shop_now_text.trim().length > 0;
}

/**
 * 判断后台是否配置了可供用户点击的跳转目标。
 * 缺少目标时顶通仅展示内容；目标非空但非法时，点击后由校验逻辑拦截并记录异常。
 */
export function hasDingTongJumpTarget(item: DingTongItem) {
  const jumpType = Number(item.jump_type);
  return Number.isInteger(jumpType) && hasValue(item.Jump_target);
}

/** 神策顶通事件的 jump_type 属性固定使用 string 类型。 */
export function getDingTongTrackingJumpType(item: DingTongItem) {
  return String(item.jump_type ?? '');
}

/** 判断顶通跳转配置是否足以交给 MagicJump 执行。 */
export function hasValidDingTongJumpTarget(item: DingTongItem) {
  const jumpType = Number(item.jump_type);
  if (!hasDingTongJumpTarget(item)) return false;

  switch (jumpType) {
    case 1:
      return true;
    case 2:
      return true;
    case 3:
      return hasValue(item.Jump_target);
    case 4: {
      if (typeof item.Jump_target !== 'string') return false;
      const target = item.Jump_target.trim();
      if (target.startsWith('/')) return true;
      try {
        const url = new URL(target);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    }
    case 5:
      return [1, 2, 3, 4].includes(Number(item.Jump_target));
    case 8:
      return true;
    default:
      return false;
  }
}

function getPageConfigs(config: DingTongConfig | null | undefined): DingTongPageConfig[] {
  if (!isRecord(config)) return [];
  const pageConfigs = config?.ding_tong_pages;
  if (Array.isArray(pageConfigs)) {
    return pageConfigs.filter(isDingTongPageConfig);
  }
  if (!pageConfigs || typeof pageConfigs !== 'object') return [];
  return Object.values(pageConfigs).filter(
    (item): item is DingTongPageConfig => !!item && typeof item === 'object',
  );
}

function getCurrentServerTime(item: DingTongItem, now: number, receivedAt: number) {
  const serverTime = toMilliseconds(item.server_time);
  if (serverTime === null) return now;
  return serverTime + Math.max(0, now - receivedAt);
}

function isExpired(item: DingTongItem, now: number, receivedAt: number) {
  const deadline = toMilliseconds(item.deadline_time_TUC_5_unix);
  if (deadline === null) return false;
  return getCurrentServerTime(item, now, receivedAt) >= deadline;
}

function getActiveItems(
  pageConfig: DingTongPageConfig | undefined,
  now: number,
  receivedAt: number,
) {
  if (!Array.isArray(pageConfig?.items)) return [];
  return pageConfig.items
    .filter(isValidDingTongItem)
    .filter((item) => !isExpired(item, now, receivedAt))
    .slice(0, MAX_VISIBLE_ITEMS);
}

export function getDingTongPage(pathname: string): DingTongPage {
  if (matchesRoute(pathname, '/goods-list') || matchesRoute(pathname, '/campaigns')) {
    return DING_TONG_PAGE.LIST_ACTIVITY;
  }
  if (matchesRoute(pathname, '/goods-detail')) return DING_TONG_PAGE.PRODUCT_DETAIL;
  if (matchesRoute(pathname, '/cart')) return DING_TONG_PAGE.CART;
  return DING_TONG_PAGE.HOME;
}

export function resolveDingTong(
  config: DingTongConfig | null | undefined,
  pathname: string,
  now: number,
  receivedAt: number,
): ResolvedDingTong | null {
  if (!config) return null;

  const pageConfigs = getPageConfigs(config);
  const targetPage = getDingTongPage(pathname);
  const targetConfig = pageConfigs.find((item) => item.page === targetPage);
  const targetItems = getActiveItems(targetConfig, now, receivedAt);

  if (targetItems.length > 0) {
    return {
      items: targetItems,
      page: targetPage,
      moduleSetting: targetConfig?.module_setting || {},
    };
  }

  if (targetPage === DING_TONG_PAGE.HOME) return null;

  const homeConfig = pageConfigs.find((item) => item.page === DING_TONG_PAGE.HOME);
  const homeItems = getActiveItems(homeConfig, now, receivedAt);
  if (homeItems.length === 0) return null;

  return {
    items: homeItems,
    page: DING_TONG_PAGE.HOME,
    moduleSetting: homeConfig?.module_setting || {},
  };
}

export function getRotationIntervalMs(moduleSetting: DingTongModuleSetting) {
  const seconds = Number(moduleSetting.rotation_interval_seconds);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
}

export function getCountdownDisplay(item: DingTongItem, now: number, receivedAt: number) {
  if (item.count_down_status !== 1 || !item.countdown) return null;

  const revealTime = toMilliseconds(item.countdown.reveals_time_unix);
  const deadline = toMilliseconds(item.deadline_time_TUC_5_unix);
  if (revealTime === null || deadline === null) return null;

  const currentServerTime = getCurrentServerTime(item, now, receivedAt);
  if (currentServerTime < revealTime || currentServerTime >= deadline) return null;

  return {
    remainingSeconds: Math.max(0, Math.ceil((deadline - currentServerTime) / 1000)),
    textColor: getSafeDingTongColor(item.countdown.text_color, DEFAULT_DING_TONG_COUNTDOWN_COLOR),
    numberColor: getSafeDingTongColor(
      item.countdown.number_color,
      DEFAULT_DING_TONG_COUNTDOWN_COLOR,
    ),
  };
}

export function getNextDingTongBoundaryDelay(
  config: DingTongConfig,
  currentItem: DingTongItem | undefined,
  now: number,
  receivedAt: number,
) {
  const delays: number[] = [];

  getPageConfigs(config).forEach((pageConfig) => {
    pageConfig.items?.forEach((item) => {
      if (!isValidDingTongItem(item)) return;
      const deadline = toMilliseconds(item.deadline_time_TUC_5_unix);
      if (deadline === null) return;
      const delay = deadline - getCurrentServerTime(item, now, receivedAt);
      if (delay > 0) delays.push(delay);
    });
  });

  if (currentItem?.count_down_status === 1 && currentItem.countdown) {
    const revealTime = toMilliseconds(currentItem.countdown.reveals_time_unix);
    if (revealTime !== null) {
      const delay = revealTime - getCurrentServerTime(currentItem, now, receivedAt);
      if (delay > 0) delays.push(delay);
    }
  }

  return delays.length > 0 ? Math.min(...delays) : null;
}

export function getDingTongJumpContent(item: DingTongItem) {
  return String(
    item.sku?.shop_sku ||
      item.product_group?.id ||
      item.crowd_recommend?.id ||
      item.Jump_target ||
      '',
  );
}
